import React, { useMemo } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Star, Eye, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Flashcard } from './Flashcard';
import api from '../api';
import { toast } from 'sonner';
import { useApp } from '../contexts/AppContext';

const typeTag = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('enseigner')) return 'mc-tag-red';
  if (t.includes('informer')) return 'mc-tag-green';
  if (t.includes('sensibiliser')) return 'mc-tag-amber';
  return 'mc-tag-blue';
};

const Checklist = ({ items, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    {(items || []).map((it, i) => (
      <div key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
        <span className="mc-dot mt-0.5 flex-shrink-0">✓</span>
        <span className="leading-snug">{it}</span>
      </div>
    ))}
    {(!items || items.length === 0) && <p className="text-xs text-muted-foreground italic">Aucun élément.</p>}
  </div>
);

const BulletList = ({ items, testId }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground" data-testid={testId}>
    {(items || []).map((it, i) => <li key={i} className="leading-snug">{it}</li>)}
    {(!items || items.length === 0) && <li className="italic">Aucun élément.</li>}
  </ul>
);

export const ModuleViewer = ({ module, onUpdated }) => {
  const { user, refreshProgress } = useApp();
  const isTeacher = user && (user.role === 'enseignant' || user.role === 'admin');

  const flashcards = useMemo(() => {
    if (!module) return [];
    return [
      { front: 'Que dois-je retenir ?', back: (module.keywords || []).join(', ') || '—' },
      { front: 'Quel est le point central ?', back: module.studentGoal || '—' },
      { front: 'Quelle erreur éviter ?', back: (module.mistakes || [])[0] || 'Rester flou sur le sujet' },
      { front: 'Quel bon réflexe ?', back: (module.success || [])[0] || 'Appliquer le contenu au réel' },
    ];
  }, [module]);

  if (!module) {
    return (
      <div className="mc-card p-6" data-testid="module-viewer-empty">
        <p className="text-muted-foreground">Sélectionnez un module dans la bibliothèque pour afficher ses contenus.</p>
      </div>
    );
  }

  const studentUnderstand = `Je dois comprendre que ce module m'apprend avant tout à mieux percevoir : ${(module.studentGoal || '').toLowerCase()}`;
  const studentRetain = `Je dois retenir les idées centrales et les mots-clés suivants : ${(module.keywords || []).slice(0, 5).join(', ')}.`;
  const studentApply = `Je dois être capable d'appliquer ce que j'ai appris dans des situations liées à : ${[...(module.plateau || []).slice(0, 2), ...(module.circulation || []).slice(0, 2)].join(', ')}.`;
  const studentCheck = `Je dois vérifier que je peux expliquer le module avec mes mots, citer les erreurs à éviter, et dire quel comportement adopter.`;

  const studentQuestions = [
    "Quel est l'objectif principal de ce module ?",
    'Quels mots-clés dois-je retenir ?',
    "Quelle erreur dois-je éviter en priorité ?",
    'Quel bon réflexe dois-je adopter ?',
    'Puis-je reformuler ce module simplement ?'
  ];
  const studentReflexes = [
    'Repérer le message sécurité principal.',
    'Faire le lien avec une situation concrète.',
    'Retenir 3 à 5 mots-clés maximum.',
    'Identifier une erreur fréquente.',
    "Être capable d'expliquer le module à voix haute."
  ];

  const handleToggleFavorite = async () => {
    if (!user) { toast.error('Connectez-vous pour utiliser les favoris'); return; }
    try {
      await api.toggleFavorite(module.id);
      toast.success(module.is_favorite ? 'Retiré des favoris' : 'Ajouté aux favoris');
      onUpdated && onUpdated();
      refreshProgress();
    } catch (e) { toast.error(e.message); }
  };
  const handleToggleSeen = async () => {
    if (!user) { toast.error('Connectez-vous pour marquer comme vu'); return; }
    try {
      await api.toggleSeen(module.id);
      toast.success(module.is_seen ? 'Marqué non vu' : 'Marqué comme vu');
      onUpdated && onUpdated();
      refreshProgress();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="mc-card p-5 sm:p-6 space-y-5" data-testid="module-viewer">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-3xl font-black tracking-tight break-words" data-testid="module-viewer-title">{module.title}</h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="module-viewer-meta">
            {module.formation} · {module.duration} · {module.family}
          </p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className={`mc-tag ${typeTag(module.type)}`} data-testid="module-viewer-type-tag">{module.type}</span>
            <span className="mc-tag mc-tag-blue">{module.formation}</span>
            <span className="mc-tag mc-tag-gray">{module.duration}</span>
          </div>
        </div>

        <div className="flex gap-2 hide-in-oral flex-shrink-0 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-2"
            onClick={handleToggleFavorite}
            data-testid="module-viewer-favorite-btn"
          >
            <Star className={`w-4 h-4 ${module.is_favorite ? 'fill-current text-[hsl(var(--primary))]' : ''}`} />
            <span>Favori</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-2"
            onClick={handleToggleSeen}
            data-testid="module-viewer-seen-btn"
          >
            {module.is_seen ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> : <Eye className="w-4 h-4" />}
            <span>{module.is_seen ? 'Déjà vu' : 'Marquer vu'}</span>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent p-0 hide-in-oral" data-testid="module-viewer-tabs">
          {[
            { v: 'overview', l: 'Résumé' },
            { v: 'student', l: 'Lecture élève' },
            { v: 'flash', l: 'Flashcards' },
            ...(isTeacher ? [
              { v: 'pedago', l: 'Pédagogie' },
              { v: 'oral', l: 'Oral enseignant' },
            ] : []),
          ].map(t => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              className="mc-tab-trigger rounded-full px-4 py-2 bg-[hsl(var(--panel))] border border-border text-foreground data-[state=active]:!text-white data-[state=active]:!border-transparent data-[state=active]:shadow-md"
              data-testid={`module-tab-${t.v}`}
            >
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="mc-soft p-4">
            <h4 className="font-bold mb-1">Vue élève</h4>
            <p className="text-sm text-muted-foreground" data-testid="module-student-goal">{module.studentGoal}</p>
            <div className="mt-3">
              <Checklist items={module.keywords} testId="module-keywords" />
            </div>
          </div>
          {isTeacher && (
            <div className="mc-soft p-4">
              <h4 className="font-bold mb-1">Vue enseignant</h4>
              <p className="text-sm text-muted-foreground" data-testid="module-teacher-goal">{module.teacherGoal}</p>
              <div className="mt-3 space-y-2">
                <Checklist items={[
                  "Analyser le public et le contexte",
                  "Définir les objectifs",
                  "Prévoir le déroulé et l'évaluation"
                ]} />
              </div>
            </div>
          )}
        </TabsContent>

        {/* Student */}
        <TabsContent value="student" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { t: 'Ce que je dois comprendre', c: studentUnderstand },
              { t: 'Ce que je dois retenir', c: studentRetain },
              { t: 'Ce que je dois appliquer', c: studentApply },
              { t: 'Ce que je dois vérifier', c: studentCheck },
            ].map((b, i) => (
              <div key={i} className="rounded-2xl p-4 border border-[hsl(var(--primary))]/20" style={{ background: 'hsl(var(--primary-soft))' }}>
                <div className="font-bold text-sm mb-1">{b.t}</div>
                <div className="text-sm text-foreground/80">{b.c}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Questions de révision élève</h5>
              <BulletList items={studentQuestions} />
            </div>
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Réflexes sécurité à mémoriser</h5>
              <BulletList items={studentReflexes} />
            </div>
          </div>
        </TabsContent>

        {/* Flashcards */}
        <TabsContent value="flash" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {flashcards.map((c, i) => (
              <Flashcard key={i} front={c.front} back={c.back} testId={`flashcard-${i}`} />
            ))}
          </div>
        </TabsContent>

        {/* Pedago — enseignants uniquement */}
        {isTeacher && (
        <TabsContent value="pedago" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="mc-soft p-4">
              <h4 className="font-bold mb-2">Théorie</h4>
              <Checklist items={module.theory} testId="module-theory" />
            </div>
            <div className="mc-soft p-4">
              <h4 className="font-bold mb-2">Plateau / pratique</h4>
              <Checklist items={module.plateau} testId="module-plateau" />
            </div>
            <div className="mc-soft p-4">
              <h4 className="font-bold mb-2">Circulation / application</h4>
              <Checklist items={module.circulation} testId="module-circulation" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Déroulé pédagogique</h5>
              <BulletList items={module.flow} />
            </div>
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Erreurs à éviter</h5>
              <BulletList items={module.mistakes} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Critères de réussite</h5>
              <BulletList items={module.success} />
            </div>
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Objectifs opérationnels</h5>
              <BulletList items={module.objectives} />
            </div>
          </div>
        </TabsContent>
        )}

        {/* Oral — enseignants uniquement */}
        {isTeacher && (
        <TabsContent value="oral" className="mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Phrases utiles à l'oral</h5>
              <BulletList items={module.juryShort} />
            </div>
            <div className="mc-card p-4">
              <h5 className="font-bold mb-2">Objectif général</h5>
              <BulletList items={[module.generalObjective]} />
            </div>
          </div>
          <div
            className="rounded-2xl p-4 border border-[hsl(var(--primary))]/20"
            style={{ background: 'linear-gradient(180deg, hsl(var(--primary-soft)), hsl(var(--card)))' }}
          >
            <h4 className="font-bold mb-1">Argumentaire enseignant</h4>
            <p className="text-sm text-foreground/80">{module.jury}</p>
          </div>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
